import { RevRecTableJournalEntry } from './RevRecDashboardTable'
import { needsClientSideSort } from '../../../utils/sortingUtils'
import { Journal } from 'RevenueRecognition/revenueRecognition.types'
import { getPeriodMonthKey } from 'RevenueRecognition/view/utils/isSamePeriod'

// Helper functions for sorting individual entries
const sortByCustomerName = (
  a: RevRecTableJournalEntry,
  b: RevRecTableJournalEntry
): number => {
  const customerNameA = a.customer?.name || ''
  const customerNameB = b.customer?.name || ''
  return customerNameA.localeCompare(customerNameB)
}

const sortByProductName = (
  a: RevRecTableJournalEntry,
  b: RevRecTableJournalEntry
): number => {
  const productNameA = a.product?.name || ''
  const productNameB = b.product?.name || ''
  return productNameA.localeCompare(productNameB)
}

const sortByPeriod = (
  a: RevRecTableJournalEntry,
  b: RevRecTableJournalEntry
): number =>
  getPeriodMonthKey(a.period).localeCompare(getPeriodMonthKey(b.period))

const sortByInvoiceName = (
  a: RevRecTableJournalEntry,
  b: RevRecTableJournalEntry
): number => {
  const invoiceNameA = a.invoice?.name || ''
  const invoiceNameB = b.invoice?.name || ''
  return invoiceNameA.localeCompare(invoiceNameB)
}

export function transformJournalData(
  journals: Journal[],
  groupBy?: 'customer' | 'product' | 'date',
  sortBy?: string
): RevRecTableJournalEntry[] {
  const result: RevRecTableJournalEntry[] = []

  // Transform each journal and its entries into table rows
  journals.forEach(journal => {
    journal.entries.forEach(entry => {
      result.push({
        id: entry.id,
        journalId: journal.id,
        period: journal.date,
        customer: journal.customer,
        product: journal.product,
        invoice: journal.invoice,
        creditNote: journal.creditNote,
        journalType: journal.journalType,
        ledgerAccount: {
          name: entry.account.name,
          debit: entry.direction === 'DEBIT' ? entry.amount : undefined,
          credit: entry.direction === 'CREDIT' ? entry.amount : undefined
        }
      })
    })
  })

  // If groupBy is set AND groupBy !== sortBy, sort client-side within groups
  // If groupBy is null, use only API sorting
  if (groupBy && needsClientSideSort(groupBy, sortBy) && sortBy) {
    // Group entries by the groupBy field
    const getGroupKey = (entry: RevRecTableJournalEntry): string => {
      switch (groupBy) {
        case 'customer':
          return entry.customer?.id || 'no-customer'
        case 'product':
          return entry.product?.id || 'no-product'
        case 'date':
          return getPeriodMonthKey(entry.period)
        default:
          return entry.journalId
      }
    }

    // Group the results while maintaining original order
    const grouped = new Map<string, RevRecTableJournalEntry[]>()
    const groupOrder: string[] = []

    result.forEach(entry => {
      const key = getGroupKey(entry)
      if (!grouped.has(key)) {
        grouped.set(key, [])
        groupOrder.push(key)
      }
      grouped.get(key)!.push(entry)
    })

    // Determine sort function based on sortBy
    const isDescending = sortBy.includes('_desc')
    let sortFunction: (
      a: RevRecTableJournalEntry,
      b: RevRecTableJournalEntry
    ) => number

    if (sortBy.includes('customer')) {
      sortFunction = sortByCustomerName
    } else if (sortBy.includes('product')) {
      sortFunction = sortByProductName
    } else if (sortBy.includes('date') || sortBy.includes('period')) {
      sortFunction = sortByPeriod
    } else if (sortBy.includes('invoice')) {
      sortFunction = sortByInvoiceName
    } else {
      // No sorting if we don't recognize the field
      return result
    }

    // Sort within each group and rebuild result
    const sortedResult: RevRecTableJournalEntry[] = []

    groupOrder.forEach(groupKey => {
      const groupEntries = grouped.get(groupKey)!
      groupEntries.sort((a, b) => {
        const comparison = sortFunction(a, b)
        return isDescending ? -comparison : comparison
      })
      sortedResult.push(...groupEntries)
    })

    return sortedResult
  }

  return result
}
